import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookmarkCheck, Loader2, MessageSquare, Send, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supplyAgentService } from '@/services/supplyAgentService';
import { SupplyAgentSavedRulesList } from '@/modules/supply/components/SupplyAgentSavedRulesList';
import type { SupplyAgentRuleRecord } from '@/modules/supply/components/supplyAgentRuleUtils';
import { labelSessionEphemeralRule, isSessionEphemeralRuleEnabled } from '@/modules/supply/components/supplyAgentSessionLabels';

interface SupplyAgentChatPanelProps {
  supplierId: number | null;
  warehouseId: number;
  savedRules?: SupplyAgentRuleRecord[];
  loadingSavedRules?: boolean;
  sessionEphemeralRules?: Record<string, unknown>[];
  onRulesChanged?: () => void;
  onApplySessionRules?: (rules: Record<string, unknown>[]) => void;
  onRemoveSessionRule?: (index: number) => void;
  onToggleSessionRule?: (index: number, enabled: boolean) => void;
  onClearSessionRules?: () => void;
  layout?: 'card' | 'drawer';
  hideSavedRules?: boolean;
  /** Oculta ajustes de sesión en el chat (viven en la columna de reglas). */
  hideSessionRules?: boolean;
  /** Si false, el chat crea/guarda reglas sin simular el pedido (módulo Reglas). */
  runSimulation?: boolean;
}

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatClarification {
  field?: string;
  kind?: string;
  question?: string;
  allow_free_text?: boolean;
  options?: Array<{ id: string; label: string }>;
}

const CONFIRM_MESSAGE_RE =
  /^(confirmo|confermo|sí|si|ok|vale|guardar|guárdalo|guardalo|salva|de acuerdo|d'?accordo|adelante)\.?$/i;
const MESSAGE_INPUT_MAX_HEIGHT_PX = 160;

function resizeMessageInput(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, MESSAGE_INPUT_MAX_HEIGHT_PX)}px`;
}

function rulesNeedConfirmation(rules: Record<string, unknown>[]): boolean {
  return rules.some((rule) => rule.requires_confirmation !== false);
}

function rulesNeedFamilyScope(rules: Record<string, unknown>[]): boolean {
  return rules.some((rule) => {
    // Primero desambiguar nombre de familia/producto.
    if (rule.needs_entity_match === true) return false;
    if (rule.needs_family_scope === true) return true;
    if (rule.family_scope_choice === 'warehouse' || rule.family_scope_choice === 'supplier') {
      return false;
    }
    const rtype = String(rule.rule_type || '');
    if (rtype.startsWith('family_')) return true;
    if (
      rtype === 'budget_limit' ||
      rtype === 'maximum_order_value' ||
      rtype === 'minimum_order_value' ||
      rtype === 'target_order_value'
    ) {
      return true;
    }
    if (rtype === 'sku_max_order_quantity') {
      const names = (rule.value as { family_names?: unknown } | undefined)?.family_names;
      return Array.isArray(names) && names.length > 0;
    }
    return false;
  });
}

function rulesNeedKeywordScope(rules: Record<string, unknown>[]): boolean {
  return rules.some(
    (rule) =>
      rule.needs_keyword_scope === true ||
      (['include_only_product_keywords', 'include_only_families'].includes(
        String(rule.rule_type || ''),
      ) &&
        rule.keyword_scope_choice !== 'warehouse' &&
        rule.keyword_scope_choice !== 'supplier'),
  );
}

function rulesNeedDistributionScope(rules: Record<string, unknown>[]): boolean {
  return rules.some((rule) => rule.needs_distribution_scope === true);
}

function rulesNeedCompletionStrategy(rules: Record<string, unknown>[]): boolean {
  return rules.some((rule) => {
    if (rule.needs_completion_strategy === true) return true;
    const rtype = String(rule.rule_type || '');
    if (rtype !== 'minimum_order_value' && rtype !== 'target_order_value' && rtype !== 'minimum_order_units') return false;
    if (rule.completion_strategy_choice) return false;
    const value =
      rule.value && typeof rule.value === 'object'
        ? (rule.value as Record<string, unknown>)
        : {};
    const applies =
      rule.applies_when && typeof rule.applies_when === 'object'
        ? (rule.applies_when as Record<string, unknown>)
        : {};
    const chosen =
      value.completion_strategy || value.strategy || applies.strategy;
    return !['top_sales', 'proportional', 'high_margin'].includes(String(chosen || ''));
  });
}

function rulesNeedEntityMatch(rules: Record<string, unknown>[]): boolean {
  return rules.some((rule) => rule.needs_entity_match === true);
}

const DISTRIBUTION_OPTION_IDS = [
  'all',
  'abc:A',
  'abc:A,B',
  'abc:B',
  'abc:B,C',
  'abc:C',
  'custom',
] as const;

function distributionOptionLabel(
  id: string,
  t: (key: string) => string,
  fallback?: string,
): string {
  const keyMap: Record<string, string> = {
    all: 'supply.supplyAgent.distributionAll',
    'abc:A': 'supply.supplyAgent.distributionA',
    'abc:A,B': 'supply.supplyAgent.distributionAB',
    'abc:B': 'supply.supplyAgent.distributionB',
    'abc:B,C': 'supply.supplyAgent.distributionBC',
    'abc:C': 'supply.supplyAgent.distributionC',
    custom: 'supply.supplyAgent.distributionCustom',
  };
  const key = keyMap[id];
  return key ? t(key) : (fallback ?? id);
}

export function SupplyAgentChatPanel({
  supplierId,
  warehouseId,
  savedRules = [],
  loadingSavedRules = false,
  sessionEphemeralRules = [],
  onRulesChanged,
  onApplySessionRules,
  onRemoveSessionRule,
  onToggleSessionRule,
  onClearSessionRules,
  layout = 'card',
  hideSavedRules = false,
  hideSessionRules = false,
  runSimulation = true,
}: SupplyAgentChatPanelProps) {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState('');
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const thinkingRef = useRef<HTMLDivElement>(null);
  const chatAbortRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pendingRules, setPendingRules] = useState<Record<string, unknown>[] | null>(null);
  const [clarification, setClarification] = useState<ChatClarification | null>(null);
  const [distributionCustomHint, setDistributionCustomHint] = useState(false);

  useEffect(() => {
    if (!loading) return;
    thinkingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [loading, turns.length]);

  const updatePendingFromInterpretation = useCallback(
    (interpretation: Record<string, unknown>) => {
      const sessionRules = interpretation.session_ephemeral_rules;
      if (Array.isArray(sessionRules) && sessionRules.length > 0) {
        onApplySessionRules?.(sessionRules as Record<string, unknown>[]);
        setPendingRules(null);
        setClarification(null);
        return;
      }
      if (interpretation.intent === 'apply_session') {
        setPendingRules(null);
        setClarification(null);
        return;
      }

      const clar = interpretation.clarification;
      const hasClarification = Boolean(clar && typeof clar === 'object');
      if (interpretation.saved && !hasClarification) {
        setPendingRules(null);
        setClarification(null);
        return;
      }
      const detected = interpretation.detected_rules;
      const rules = (Array.isArray(detected) ? detected : []) as Record<string, unknown>[];
      if (
        rules.length > 0 &&
        (rulesNeedConfirmation(rules) ||
          rulesNeedFamilyScope(rules) ||
          rulesNeedDistributionScope(rules) ||
          rulesNeedKeywordScope(rules) ||
          hasClarification)
      ) {
        setPendingRules(rules);
      } else if (!hasClarification) {
        setPendingRules(null);
      }
      if (hasClarification) {
        setClarification(clar as ChatClarification);
        if ((clar as ChatClarification).field !== 'distribution_scope') {
          setDistributionCustomHint(false);
        }
      } else {
        setClarification(null);
        setDistributionCustomHint(false);
      }
    },
    [onApplySessionRules],
  );

  const chatErrorMessage = useCallback(
    (e: unknown) => {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return t('supply.supplyAgent.timeout');
      }
      if (e instanceof Error && e.message) return e.message;
      if (e && typeof e === 'object' && 'message' in e) {
        const msg = (e as { message?: unknown }).message;
        if (typeof msg === 'string' && msg.trim()) return msg;
      }
      return t('supply.supplyAgent.error');
    },
    [t],
  );

  const cancelChat = useCallback(() => {
    chatAbortRef.current?.abort();
    chatAbortRef.current = null;
    setLoading(false);
  }, []);

  const sendChat = useCallback(
    async (text: string, options?: { confirmSave?: boolean; pending?: Record<string, unknown>[] | null }) => {
      const confirmSave = Boolean(
        options?.confirmSave ||
          (options?.pending && CONFIRM_MESSAGE_RE.test(text.trim())),
      );
      const pending = options?.pending !== undefined ? options.pending : pendingRules;
      const conversation_history = turns.slice(-4).map((turn) => ({
        role: turn.role,
        content: turn.content,
      }));

      chatAbortRef.current?.abort();
      const controller = new AbortController();
      chatAbortRef.current = controller;
      const timeoutMs = confirmSave ? 30_000 : 90_000;
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

      try {
        const { reply, interpretation } = await supplyAgentService.chat({
          message: text,
          ...(supplierId != null ? { supplier_id: supplierId } : {}),
          warehouse_id: warehouseId,
          use_llm: !confirmSave,
          run_simulation: runSimulation && !confirmSave && supplierId != null,
          confirm_save: confirmSave,
          pending_rules: pending ?? undefined,
          locale: i18n.language,
          conversation_history,
          signal: controller.signal,
        });

        setTurns((prev) => [...prev, { role: 'assistant', content: reply }]);
        updatePendingFromInterpretation(interpretation);
        if (interpretation.saved) {
          onRulesChanged?.();
        }
        return interpretation;
      } finally {
        window.clearTimeout(timeoutId);
        if (chatAbortRef.current === controller) {
          chatAbortRef.current = null;
        }
      }
    },
    [supplierId, warehouseId, pendingRules, updatePendingFromInterpretation, onRulesChanged, i18n.language, turns],
  );

  const sendMessage = useCallback(async () => {
    const text = message.trim();
    if (!text || loading) return;
    setLoading(true);
    setError(null);
    setMessage('');
    setTurns((prev) => [...prev, { role: 'user', content: text }]);
    try {
      await sendChat(text, { pending: pendingRules });
    } catch (e) {
      const errText = chatErrorMessage(e);
      setError(errText);
      setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
    } finally {
      setLoading(false);
    }
  }, [message, loading, sendChat, pendingRules, chatErrorMessage]);

  const chooseFamilyScope = useCallback(
    async (choice: 'warehouse' | 'supplier') => {
      if (!pendingRules?.length || loading) return;
      if (choice === 'supplier' && supplierId == null) {
        setError(t('supply.supplyAgent.needSupplierFilter'));
        return;
      }
      const enriched = pendingRules.map((rule) => {
        const rtype = String(rule.rule_type || '');
        const names = (rule.value as { family_names?: unknown } | undefined)?.family_names;
        const hasFamilyNames = Array.isArray(names) && names.length > 0;
        const applies =
          rtype.startsWith('family_') ||
          rtype === 'budget_limit' ||
          rtype === 'maximum_order_value' ||
          rtype === 'minimum_order_value' ||
          rtype === 'target_order_value' ||
          (rtype === 'sku_max_order_quantity' && hasFamilyNames) ||
          rule.needs_family_scope === true;
        if (!applies) return rule;
        return {
          ...rule,
          family_scope_choice: choice,
          needs_family_scope: false,
          scope: choice,
          scope_ref:
            choice === 'warehouse'
              ? { warehouse_id: warehouseId }
              : supplierId != null
                ? { warehouse_id: warehouseId, supplier_id: supplierId }
                : { warehouse_id: warehouseId },
        };
      });
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      const label =
        choice === 'warehouse'
          ? t('supply.supplyAgent.familyScopeWarehouse')
          : t('supply.supplyAgent.familyScopeSupplier');
      setTurns((prev) => [...prev, { role: 'user', content: label }]);
      try {
        await sendChat(label, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, supplierId, warehouseId, t, chatErrorMessage],
  );

  const chooseKeywordScope = useCallback(
    async (choice: 'warehouse' | 'supplier') => {
      if (!pendingRules?.length || loading) return;
      if (choice === 'supplier' && supplierId == null) {
        setError(t('supply.supplyAgent.needSupplierFilter'));
        return;
      }
      const enriched = pendingRules.map((rule) => {
        const rtype = String(rule.rule_type || '');
        if (
          rtype !== 'include_only_product_keywords' &&
          rtype !== 'include_only_families'
        ) {
          return rule;
        }
        return {
          ...rule,
          keyword_scope_choice: choice,
          needs_keyword_scope: false,
          scope: choice,
          scope_ref:
            choice === 'warehouse'
              ? { warehouse_id: warehouseId }
              : { warehouse_id: warehouseId, supplier_id: supplierId },
        };
      });
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      const label =
        choice === 'warehouse'
          ? t('supply.supplyAgent.familyScopeWarehouse')
          : t('supply.supplyAgent.familyScopeSupplier');
      setTurns((prev) => [...prev, { role: 'user', content: label }]);
      try {
        await sendChat(label, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, supplierId, warehouseId, t, chatErrorMessage],
  );

  const chooseDistributionScope = useCallback(
    async (optionId: string, optionLabel: string) => {
      if (optionId === 'custom') {
        setDistributionCustomHint(true);
        messageInputRef.current?.focus();
        return;
      }
      setDistributionCustomHint(false);
      if (!pendingRules?.length || loading) return;
      const enriched = pendingRules.map((rule) => {
        const rtype = String(rule.rule_type || '');
        if (!rtype.startsWith('family_') && rule.needs_distribution_scope !== true) {
          return rule;
        }
        const prevValue =
          rule.value && typeof rule.value === 'object'
            ? (rule.value as Record<string, unknown>)
            : {};
        const nextValue = { ...prevValue };
        if (optionId === 'all') {
          nextValue.distribution_scope = 'all';
          nextValue.abc_categories = [];
        } else if (optionId.startsWith('abc:')) {
          nextValue.distribution_scope = 'abc';
          nextValue.abc_categories = optionId
            .slice(4)
            .split(',')
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean);
        }
        return {
          ...rule,
          value: nextValue,
          distribution_scope_choice: optionId,
          needs_distribution_scope: false,
        };
      });
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      setTurns((prev) => [...prev, { role: 'user', content: optionLabel }]);
      try {
        await sendChat(optionLabel, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, t, chatErrorMessage],
  );

  const chooseCompletionStrategy = useCallback(
    async (optionId: string, optionLabel: string) => {
      if (!pendingRules?.length || loading) return;
      const enriched = pendingRules.map((rule) => {
        const rtype = String(rule.rule_type || '');
        if (
          rtype !== 'minimum_order_value' &&
          rtype !== 'target_order_value' &&
          rtype !== 'minimum_order_units' &&
          rule.needs_completion_strategy !== true
        ) {
          return rule;
        }
        const prevValue =
          rule.value && typeof rule.value === 'object'
            ? (rule.value as Record<string, unknown>)
            : {};
        const prevApplies =
          rule.applies_when && typeof rule.applies_when === 'object'
            ? (rule.applies_when as Record<string, unknown>)
            : {};
        return {
          ...rule,
          value: {
            ...prevValue,
            completion_strategy: optionId,
            strategy: optionId,
          },
          applies_when: {
            ...prevApplies,
            strategy: optionId,
          },
          completion_strategy_choice: optionId,
          needs_completion_strategy: false,
        };
      });
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      setTurns((prev) => [...prev, { role: 'user', content: optionLabel }]);
      try {
        await sendChat(optionLabel, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, chatErrorMessage],
  );

  const chooseLogisticsStrategy = useCallback(
    async (optionId: string, optionLabel: string) => {
      if (loading) return;
      const base =
        pendingRules?.length
          ? pendingRules
          : [
              {
                rule_type: 'logistics_tool',
                needs_logistics_strategy: true,
              } as Record<string, unknown>,
            ];
      const enriched = base.map((rule) => {
        if (String(rule.rule_type || '') !== 'logistics_tool' && rule.needs_logistics_strategy !== true) {
          return rule;
        }
        const prevValue =
          rule.value && typeof rule.value === 'object'
            ? (rule.value as Record<string, unknown>)
            : {};
        return {
          ...rule,
          rule_type: 'logistics_tool',
          value: {
            ...prevValue,
            optimization_strategy: optionId,
          },
          logistics_strategy_choice: optionId,
          needs_logistics_strategy: false,
        };
      });
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      setTurns((prev) => [...prev, { role: 'user', content: optionLabel }]);
      try {
        await sendChat(optionLabel, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, chatErrorMessage],
  );

  const chooseEntityMatch = useCallback(
    async (optionId: string, optionLabel: string) => {
      if (!pendingRules?.length || loading) return;
      const enriched = pendingRules.map((rule) => {
        if (rule.needs_entity_match !== true) return rule;
        const kind = String(rule.entity_match_kind || 'family');
        const prevValue =
          rule.value && typeof rule.value === 'object'
            ? (rule.value as Record<string, unknown>)
            : {};
        const canonical =
          optionLabel.includes(' (') && optionLabel.endsWith(')')
            ? optionLabel.slice(0, optionLabel.lastIndexOf(' (')).trim() || optionId
            : optionId;
        const nextValue = { ...prevValue };
        if (kind === 'product') {
          const rtype = String(rule.rule_type || '');
          if (rtype === 'include_only_product_keywords') {
            nextValue.keywords = [canonical];
            nextValue.keyword_variants = [canonical];
          } else {
            nextValue.product_keywords = [canonical];
          }
        } else {
          nextValue.family_names = [canonical];
        }
        return {
          ...rule,
          value: nextValue,
          entity_match_choice: optionId,
          needs_entity_match: false,
        };
      });
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      setTurns((prev) => [...prev, { role: 'user', content: optionLabel }]);
      try {
        await sendChat(optionLabel, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, chatErrorMessage],
  );

  const chooseConflictResolution = useCallback(
    async (optionId: string, optionLabel: string) => {
      if (!pendingRules?.length || loading) return;
      const enriched = pendingRules.map((rule) => ({
        ...rule,
        conflict_resolution_choice: optionId,
        conflict_kind: clarification?.kind,
      }));
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      setTurns((prev) => [...prev, { role: 'user', content: optionLabel }]);
      try {
        await sendChat(optionLabel, { pending: enriched });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, clarification?.kind, t, chatErrorMessage],
  );

  const choosePersistence = useCallback(
    async (choice: 'this_order' | 'always') => {
      if (!pendingRules?.length || loading) return;
      const enriched = pendingRules.map((rule) => ({
        ...rule,
        persistence_choice: choice,
        needs_persistence_choice: false,
        is_persistent: choice === 'always',
        requires_confirmation: choice === 'always',
      }));
      setPendingRules(enriched);
      setLoading(true);
      setError(null);
      const label =
        choice === 'this_order'
          ? t('supply.supplyAgent.persistenceThisOrder')
          : t('supply.supplyAgent.persistenceAlways');
      setTurns((prev) => [...prev, { role: 'user', content: label }]);
      try {
        await sendChat(label, {
          confirmSave: choice === 'always',
          pending: enriched,
        });
      } catch (e) {
        const errText = chatErrorMessage(e);
        setError(errText);
        setTurns((prev) => [...prev, { role: 'assistant', content: errText }]);
      } finally {
        setLoading(false);
      }
    },
    [pendingRules, loading, sendChat, t, chatErrorMessage],
  );

  const discardPending = useCallback(() => {
    setPendingRules(null);
    setClarification(null);
    setError(null);
  }, []);

  const clearChat = useCallback(() => {
    setTurns([]);
    setPendingRules(null);
    setClarification(null);
    setError(null);
    setMessage('');
  }, []);

  const canClearChat =
    turns.length > 0 ||
    Boolean(pendingRules?.length) ||
    clarification != null ||
    error != null;

  useEffect(() => {
    resizeMessageInput(messageInputRef.current);
  }, [message]);

  const showEntityMatch =
    Boolean(clarification?.field === 'entity_match') ||
    Boolean(pendingRules && rulesNeedEntityMatch(pendingRules));
  const showFamilyScope =
    !showEntityMatch &&
    (Boolean(clarification?.field === 'family_scope') ||
      Boolean(pendingRules && rulesNeedFamilyScope(pendingRules)));
  const showDistributionScope =
    !showEntityMatch &&
    !showFamilyScope &&
    (Boolean(clarification?.field === 'distribution_scope') ||
      Boolean(pendingRules && rulesNeedDistributionScope(pendingRules)));
  const showCompletionStrategy =
    !showEntityMatch &&
    !showFamilyScope &&
    !showDistributionScope &&
    (Boolean(clarification?.field === 'completion_strategy') ||
      Boolean(pendingRules && rulesNeedCompletionStrategy(pendingRules)));
  const showLogisticsStrategy =
    !showEntityMatch &&
    !showFamilyScope &&
    !showDistributionScope &&
    !showCompletionStrategy &&
    (Boolean(clarification?.field === 'logistics_strategy') ||
      Boolean(pendingRules?.some((r) => r.needs_logistics_strategy === true)));
  const showKeywordScope =
    !showEntityMatch &&
    !showFamilyScope &&
    !showDistributionScope &&
    !showCompletionStrategy &&
    !showLogisticsStrategy &&
    (Boolean(clarification?.field === 'keyword_scope') ||
      Boolean(pendingRules && rulesNeedKeywordScope(pendingRules)));
  const showRuleConflict =
    !showEntityMatch &&
    !showFamilyScope &&
    !showDistributionScope &&
    !showCompletionStrategy &&
    !showLogisticsStrategy &&
    !showKeywordScope &&
    Boolean(clarification?.field === 'rule_conflict');
  const showPersistence =
    !showEntityMatch &&
    !showFamilyScope &&
    !showDistributionScope &&
    !showCompletionStrategy &&
    !showLogisticsStrategy &&
    !showKeywordScope &&
    !showRuleConflict &&
    (Boolean(clarification?.field === 'persistence_scope') || Boolean(pendingRules?.length));
  const entityMatchOptions =
    clarification?.field === 'entity_match' && clarification.options?.length
      ? clarification.options
      : (pendingRules || [])
          .flatMap((rule) =>
            Array.isArray(rule.entity_match_candidates)
              ? (rule.entity_match_candidates as Array<{ id: string; label: string }>)
              : [],
          )
          .filter((opt, idx, arr) => arr.findIndex((o) => o.id === opt.id) === idx);
  const conflictOptions = clarification?.options || [];
  const defaultCompletionOptions = useMemo(
    () => [
      {
        id: 'coverage_layers',
        label: t('supply.supplyAgent.completionStrategyCoverage'),
      },
      {
        id: 'top_sales',
        label: t('supply.supplyAgent.completionStrategyTopSales'),
      },
      {
        id: 'proportional',
        label: t('supply.supplyAgent.completionStrategyProportional'),
      },
      {
        id: 'high_margin',
        label: t('supply.supplyAgent.completionStrategyHighMargin'),
      },
    ],
    [t],
  );
  const completionOptions =
    clarification?.field === 'completion_strategy' && clarification.options?.length
      ? clarification.options
      : defaultCompletionOptions;
  const defaultDistributionOptions = useMemo(
    () =>
      DISTRIBUTION_OPTION_IDS.map((id) => ({
        id,
        label: distributionOptionLabel(id, t),
      })),
    [t],
  );
  const distributionOptions =
    clarification?.field === 'distribution_scope' && clarification.options?.length
      ? clarification.options.map((opt) => ({
          ...opt,
          label: distributionOptionLabel(opt.id, t, opt.label),
        }))
      : defaultDistributionOptions;
  const distributionPresetOptions = distributionOptions.filter((opt) => opt.id !== 'custom');
  const distributionCustomOption = distributionOptions.find((opt) => opt.id === 'custom');
  const showDistributionFreeTextHint =
    distributionCustomHint ||
    clarification?.allow_free_text === true ||
    Boolean(distributionCustomOption);

  const isDrawer = layout === 'drawer';

  return (
    <div
      className={
        isDrawer
          ? 'flex h-full min-h-0 flex-col bg-white'
          : 'rounded-lg border border-gray-200 bg-white p-3 shadow-sm'
      }
    >
      {!isDrawer && (
        <>
          <div className="mb-2 flex items-start justify-between gap-2">
            <h4 className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-sky-600" />
              {t('supply.supplyAgent.chatTitle')}
            </h4>
            {canClearChat && (
              <button
                type="button"
                onClick={clearChat}
                disabled={loading}
                className="shrink-0 text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
              >
                {t('supply.supplyAgent.clearChat')}
              </button>
            )}
          </div>
        </>
      )}

      <div className={isDrawer ? 'min-h-0 flex-1 overflow-y-auto px-4 pt-3' : undefined}>
        {isDrawer && canClearChat && (
          <div className="mb-3 flex items-start justify-end">
            <button
              type="button"
              onClick={clearChat}
              disabled={loading}
              className="shrink-0 text-xs font-medium text-slate-500 hover:text-slate-800 disabled:opacity-50"
            >
              {t('supply.supplyAgent.clearChat')}
            </button>
          </div>
        )}

        {!hideSavedRules && (
          <div className="mb-2">
            <SupplyAgentSavedRulesList
              rules={savedRules}
              loading={loadingSavedRules}
              compact
            />
          </div>
        )}

        {!hideSessionRules && sessionEphemeralRules.length > 0 && (
          <div className="mb-2 rounded-md border border-sky-100 bg-sky-50 px-2 py-1.5">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-sky-900">
                {t('supply.supplyAgent.sessionEphemeralTitle', {
                  count: sessionEphemeralRules.length,
                })}
              </p>
              {sessionEphemeralRules.length > 1 && (
                <button
                  type="button"
                  onClick={() => onClearSessionRules?.()}
                  className="shrink-0 text-xs font-medium text-sky-700 hover:text-sky-900"
                >
                  {t('supply.supplyAgent.clearAllSessionEphemeral')}
                </button>
              )}
            </div>
            <p className="mb-1 text-[10px] leading-snug text-sky-700/90">
              {t('supply.supplyAgent.sessionEphemeralToggleHint')}
            </p>
            <ul className="space-y-1">
              {sessionEphemeralRules.map((rule, idx) => {
                const enabled = isSessionEphemeralRuleEnabled(rule);
                return (
                  <li
                    key={`${String(rule.rule_type || 'rule')}-${idx}`}
                    className={`flex items-center justify-between gap-2 text-xs ${
                      enabled ? 'text-sky-900' : 'text-sky-500'
                    }`}
                  >
                    <label className="flex min-w-0 flex-1 items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-3 w-3 shrink-0 accent-sky-600"
                        checked={enabled}
                        onChange={(e) => onToggleSessionRule?.(idx, e.target.checked)}
                        aria-label={t('supply.supplyAgent.sessionToggle')}
                      />
                      <span className={`truncate ${enabled ? '' : 'opacity-70 line-through'}`}>
                        {labelSessionEphemeralRule(rule)}
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => onRemoveSessionRule?.(idx)}
                      className="inline-flex shrink-0 items-center gap-0.5 text-sky-700 hover:text-sky-900"
                      title={t('supply.supplyAgent.removeSessionEphemeral')}
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('supply.supplyAgent.removeSessionEphemeral')}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {turns.length === 0 && !loading ? (
          <p className={isDrawer ? 'mb-3 text-sm text-slate-500' : 'mb-2 text-xs text-gray-500'}>
            {t('supply.supplyAgent.chatHint')}
          </p>
        ) : null}

        {(turns.length > 0 || loading) && (
          <div
            className={
              isDrawer
                ? 'mb-2 space-y-2.5'
                : 'mb-2 max-h-40 overflow-y-auto space-y-2 rounded-md bg-gray-50 p-2 text-xs'
            }
          >
            {turns.map((turn, idx) => (
              <div
                key={idx}
                className={
                  isDrawer
                    ? turn.role === 'user'
                      ? 'ml-6 rounded-2xl rounded-br-md bg-sky-600 px-3 py-2 text-sm text-white'
                      : 'mr-6 rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2 text-sm text-slate-800'
                    : turn.role === 'user'
                      ? 'text-gray-800'
                      : 'text-gray-600'
                }
              >
                {!isDrawer && (
                  <span className="font-medium">
                    {turn.role === 'user' ? 'Tú' : 'Agente'}:{' '}
                  </span>
                )}
                <span className="whitespace-pre-wrap">{turn.content}</span>
              </div>
            ))}
            {loading ? (
              <div
                ref={thinkingRef}
                className={
                  isDrawer
                    ? 'mr-6 inline-flex flex-wrap items-center gap-2 rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2 text-sm text-slate-600'
                    : 'inline-flex flex-wrap items-center gap-2 text-sm text-gray-500'
                }
                aria-live="polite"
                aria-busy="true"
              >
                <span>{t('supply.supplyAgent.thinking')}</span>
                <span className="inline-flex gap-0.5" aria-hidden>
                  <span className="h-1 w-1 animate-bounce rounded-full bg-sky-500 [animation-delay:0ms]" />
                  <span className="h-1 w-1 animate-bounce rounded-full bg-sky-500 [animation-delay:150ms]" />
                  <span className="h-1 w-1 animate-bounce rounded-full bg-sky-500 [animation-delay:300ms]" />
                </span>
                <button
                  type="button"
                  onClick={cancelChat}
                  className="ml-1 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-slate-700 hover:bg-white"
                >
                  {t('supply.supplyAgent.cancelThinking')}
                </button>
              </div>
            ) : null}
          </div>
        )}

      {pendingRules && pendingRules.length > 0 && (
        <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 p-2">
          <p className="text-xs text-amber-900 mb-2">
            {showEntityMatch
              ? clarification?.question || t('supply.supplyAgent.entityMatchQuestion')
              : showFamilyScope
              ? t('supply.supplyAgent.familyScopeQuestion')
              : showDistributionScope
                ? t('supply.supplyAgent.distributionScopeQuestion')
                : showCompletionStrategy
                  ? clarification?.question ||
                    t('supply.supplyAgent.completionStrategyQuestion')
                  : showLogisticsStrategy
                    ? clarification?.question ||
                      t('supply.supplyAgent.logisticsStrategyQuestion')
                  : showKeywordScope
                    ? t('supply.supplyAgent.keywordScopeQuestion')
                    : showRuleConflict
                      ? clarification?.question || t('supply.supplyAgent.conflictQuestion')
                      : t('supply.supplyAgent.persistenceQuestion')}
          </p>
          {showEntityMatch ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {entityMatchOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => void chooseEntityMatch(opt.id, opt.label)}
                    disabled={loading}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {opt.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={discardPending}
                  disabled={loading}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                  {t('supply.supplyAgent.discardPending')}
                </button>
              </div>
              <p className="text-[11px] text-amber-800">
                {t('supply.supplyAgent.entityMatchHint')}
              </p>
            </div>
          ) : showFamilyScope ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void chooseFamilyScope('warehouse')}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {t('supply.supplyAgent.familyScopeWarehouse')}
              </button>
              <button
                type="button"
                onClick={() => void chooseFamilyScope('supplier')}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {t('supply.supplyAgent.familyScopeSupplier')}
              </button>
              <button
                type="button"
                onClick={discardPending}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {t('supply.supplyAgent.discardPending')}
              </button>
            </div>
          ) : showDistributionScope ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {distributionPresetOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => void chooseDistributionScope(opt.id, opt.label)}
                    disabled={loading}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {opt.label}
                  </button>
                ))}
                {distributionCustomOption ? (
                  <button
                    type="button"
                    onClick={() => void chooseDistributionScope('custom', distributionCustomOption.label)}
                    disabled={loading}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-sky-300 bg-white text-sky-800 hover:bg-sky-50 disabled:opacity-50"
                  >
                    {distributionCustomOption.label}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={discardPending}
                  disabled={loading}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                  {t('supply.supplyAgent.discardPending')}
                </button>
              </div>
              {showDistributionFreeTextHint ? (
                <p className="text-xs text-amber-800/90">
                  {distributionCustomHint
                    ? t('supply.supplyAgent.distributionCustomHintActive')
                    : t('supply.supplyAgent.distributionCustomHint')}
                </p>
              ) : null}
            </div>
          ) : showCompletionStrategy ? (
            <div className="flex flex-wrap gap-2">
              {completionOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => void chooseCompletionStrategy(opt.id, opt.label)}
                  disabled={loading}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium disabled:opacity-50 ${
                    opt.id === 'coverage_layers'
                      ? 'bg-sky-600 text-white hover:bg-sky-700'
                      : 'border border-sky-300 bg-white text-sky-900 hover:bg-sky-50'
                  }`}
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {opt.label}
                </button>
              ))}
              <button
                type="button"
                onClick={discardPending}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {t('supply.supplyAgent.discardPending')}
              </button>
            </div>
          ) : showLogisticsStrategy ? (
            <div className="flex flex-wrap gap-2">
              {(clarification?.options || []).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => void chooseLogisticsStrategy(opt.id, opt.label)}
                  disabled={loading}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium disabled:opacity-50 ${
                    opt.id === 'ROTATION_WEIGHTED_FILL'
                      ? 'bg-sky-600 text-white hover:bg-sky-700'
                      : 'border border-sky-300 bg-white text-sky-900 hover:bg-sky-50'
                  }`}
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {opt.label}
                </button>
              ))}
              <button
                type="button"
                onClick={discardPending}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {t('supply.supplyAgent.discardPending')}
              </button>
            </div>
          ) : showKeywordScope ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void chooseKeywordScope('warehouse')}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {t('supply.supplyAgent.familyScopeWarehouse')}
              </button>
              <button
                type="button"
                onClick={() => void chooseKeywordScope('supplier')}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {t('supply.supplyAgent.familyScopeSupplier')}
              </button>
              <button
                type="button"
                onClick={discardPending}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {t('supply.supplyAgent.discardPending')}
              </button>
            </div>
          ) : showRuleConflict ? (
            <div className="flex flex-wrap gap-2">
              {conflictOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => void chooseConflictResolution(opt.id, opt.label)}
                  disabled={loading}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {opt.label}
                </button>
              ))}
              <button
                type="button"
                onClick={discardPending}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {t('supply.supplyAgent.discardPending')}
              </button>
            </div>
          ) : showPersistence ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void choosePersistence('this_order')}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {t('supply.supplyAgent.persistenceThisOrder')}
              </button>
              <button
                type="button"
                onClick={() => void choosePersistence('always')}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <BookmarkCheck className="w-3.5 h-3.5" />
                )}
                {t('supply.supplyAgent.persistenceAlways')}
              </button>
              <button
                type="button"
                onClick={discardPending}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                {t('supply.supplyAgent.discardPending')}
              </button>
            </div>
          ) : null}
        </div>
      )}

        {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
      </div>

      <div
        className={
          isDrawer
            ? 'shrink-0 border-t border-slate-200 bg-white px-4 py-3'
            : undefined
        }
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={messageInputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void sendMessage();
              }
            }}
            rows={1}
            placeholder={t('supply.supplyAgent.chatPlaceholder')}
            className="flex-1 min-h-[2.125rem] max-h-40 resize-none overflow-y-auto rounded-md border border-gray-300 px-2 py-1.5 text-sm leading-snug"
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => void sendMessage()}
            disabled={loading || !message.trim()}
            className="inline-flex shrink-0 items-center gap-1 rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
